import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { AssetContextService } from '../../services/asset-context.service';
import { AuthService } from '../../services/auth.service';
import { AccessRightDomainStore } from '../../stores/access-right.store';
import { AssetGroupStore } from '../../stores/asset-group.store';
import { AssetStore } from '../../stores/asset.store';
import { UserStore } from '../../stores/user.store';
import { AssetGroup } from '../../types/asset-group.interface';
import { Asset } from '../../types/asset.interface';
import { PermissionScope } from '../../types/permission-scope.enum';

interface AssetSelectItem {
  label: string;
  value: number;
}

interface AssetGroupSelectItem {
  label: string;
  value: number;
  items: AssetSelectItem[];
}

@Component({
  selector: 'app-test-permission',
  imports: [ButtonModule, HasPermissionDirective, SelectModule, CommonModule, FormsModule],
  templateUrl: './test-permission.component.html',
  styleUrl: './test-permission.component.css',
  standalone: true,
})
export class TestPermissionComponent implements OnInit {
  protected readonly PermissionScope = PermissionScope;

  private assetContext = inject(AssetContextService);
  private authService = inject(AuthService);
  private assetStore = inject(AssetStore);
  private assetGroupStore = inject(AssetGroupStore);
  private accessRightStore = inject(AccessRightDomainStore);
  private userStore = inject(UserStore);

  private savedAssetId: number | null = null;

  currentAssetId = this.assetContext.currentAsset;
  currentUserId = this.authService.currentUserId;
  groupedAssets: AssetGroupSelectItem[] = [];
  selectedAssetId: number | null = null;
  availableUsers: { label: string; value: number }[] = [];
  readonly testUserId = signal<number | null>(null);

  ngOnInit(): void {
    void this.initializeView();
  }

  private async initializeView(): Promise<void> {
    await Promise.all([this.assetGroupStore.loadAll(), this.assetStore.loadAll(), this.userStore.loadAll()]);

    this.prepareUsers();
    await new Promise(resolve => setTimeout(resolve, 0));

    const realUserId = this.authService.currentUserId();
    if (realUserId) {
      await this.accessRightStore.loadByUserId(realUserId);
      this.testUserId.set(realUserId);
    }

    this.prepareAllowedAssets();
    this.selectedAssetId = this.assetContext.getCurrentAssetId();
  }

  private prepareUsers(): void {
    const users = this.userStore.entities();
    this.availableUsers = users.map(user => ({
      label: `${user.firstname} ${user.name} (${user.email})`,
      value: user.id,
    }));
  }

  private prepareAllowedAssets(): void {
    const groups: AssetGroup[] = this.assetGroupStore.entities();
    const allAssets: Asset[] = this.assetStore.entities();
    const allowedAssets = this.filterAssetsByPermissions(allAssets);

    this.groupedAssets = groups
      .map((group: AssetGroup) => {
        const groupAssets: Asset[] = allowedAssets.filter((asset: Asset) => asset.assetGroupId === group.id);

        return {
          label: group.name,
          value: group.id,
          items: groupAssets.map(asset => ({
            label: asset.name,
            value: asset.id,
          })),
        };
      })
      .filter((group: AssetGroupSelectItem) => group.items.length > 0);
  }

  private filterAssetsByPermissions(assets: Asset[]): Asset[] {
    const userId = this.testUserId();

    if (!userId) {
      return assets;
    }

    const user = this.userStore.entities().find(u => u.id === userId);
    if (user?.isAdmin) {
      return assets;
    }

    const userAccessRights = this.accessRightStore.entities().filter(ar => ar.userId === userId);
    const allowedAssetIds = new Set(userAccessRights.map(ar => ar.assetId));

    return assets.filter(asset => allowedAssetIds.has(asset.id));
  }

  async onUserSelected(event: SelectChangeEvent): Promise<void> {
    const userId = event.value as unknown;
    if (typeof userId === 'number' && userId !== 0) {
      this.testUserId.set(userId);
      await this.accessRightStore.loadByUserId(userId);
      this.prepareAllowedAssets();
    }
  }

  onAssetSelected(event: SelectChangeEvent): void {
    if (event.value && typeof event.value === 'number') {
      this.assetContext.setCurrentAsset(event.value);
      this.selectedAssetId = event.value;
    }
  }

  clearAssetContext(): void {
    this.savedAssetId = this.assetContext.getCurrentAssetId();
    this.assetContext.clearContext();
    this.selectedAssetId = null;
  }

  restoreAssetContext(): void {
    if (this.savedAssetId) {
      this.assetContext.setCurrentAsset(this.savedAssetId);
      this.selectedAssetId = this.savedAssetId;
    }
  }
}
